import { FooterLink, FooterPageContent, TermsAndConditionsCardProps, URL_PERMISSIONS_MAPPING_TYPE } from "@/types";

export const UN_AUTHENTICATED = "unauthenticated";
export const AUTHENTICATED = "authenticated";

export const CONFIRM_BUTTON = "Confirm";
export const CANCEL_BUTTON = "Cancel";
export const CLOSE_BUTTON = "Close";
export const RETURN_BUTTON = "No, return";
export const YES_CANCEL_BUTTON = "Yes, cancel";

export const CONVERSATION_EVENT_TYPES = {
  CHAT: "chat",
  SCENE_GENERATION: "scene_generation",
  REPLACE_ALL: "replace_all",
  FRAME_EDIT: "frame_edit",
  ADD_FRAME: "add_frame",
};

export const HTTP_METHOD = {
  GET: "GET",
  POST: "POST",
  PUT: "PUT",
  PATCH: "PATCH",
  DELETE: "DELETE",
} as const;

export const ENVIRONMENT = {
  DEVELOPMENT: "development",
  PRODUCTION: "production",
} as const;

export const FILE_TYPE = {
  PDF: "pdf",
  JSON: "json",
  XLSX: "xlsx",
  XLS: "xls",
  JPG: "jpg",
  JPEG: "jpeg",
  PNG: "png",
  PSD: "psd",
  TXT: "txt",
  CSV: "csv",
  TTF: "ttf",
  HTML: "html",
};

export const FILE_EXTENSION = {
  PDF: ".pdf",
  JSON: ".json",
  XLSX: ".xlsx",
  XLS: ".xls",
  JPG: ".jpg",
  JPEG: ".jpeg",
  PNG: ".png",
  PSD: ".psd",
  TXT: ".txt",
  CSV: ".csv",
  TTF: ".ttf",
  HTML: ".html",
};

export const STATUS_TYPE = {
  PENDING: "pending",
  FAILED: "failed",
  SUCCESSFUL: "successful",
  FAILURE: "failure",
  RUNNING: "running",
  UNKNOWN: "unknown",
} as const;

export const REFERENCE_ASSET_METADATA_TYPE = {
  LOGO: "Logo",
  FONT: "Font",
  PRODUCT_IMAGE: "ProductPlacementImage",
};

export const CONTENT_TYPE = {
  APPLICATION: {
    JSON: "application/json",
    PDF: "application/pdf",
    PNG: "image/png",
    JPG: "image/jpeg",
    XLS: "application/vnd.ms-excel",
    XLSX: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    CSV: "text/csv",
    TXT: "text/plain",
  },
};

export const FILE_CONFIG = {
  [FILE_TYPE.JPEG]: {
    extensions: [FILE_EXTENSION.JPG, FILE_EXTENSION.JPEG],
    mimeTypes: [CONTENT_TYPE.APPLICATION.JPG],
  },
  [FILE_TYPE.PNG]: {
    extensions: [FILE_EXTENSION.PNG],
    mimeTypes: [CONTENT_TYPE.APPLICATION.PNG],
  },
  [FILE_TYPE.PDF]: {
    extensions: [FILE_EXTENSION.PDF],
    mimeTypes: [CONTENT_TYPE.APPLICATION.PDF],
  },
  [FILE_TYPE.XLSX]: {
    extensions: [FILE_EXTENSION.XLSX],
    mimeTypes: [CONTENT_TYPE.APPLICATION.XLSX],
  },
  [FILE_TYPE.XLS]: {
    extensions: [FILE_EXTENSION.XLS],
    mimeTypes: [CONTENT_TYPE.APPLICATION.XLS],
  },
  [FILE_TYPE.CSV]: {
    extensions: [FILE_EXTENSION.CSV],
    mimeTypes: [CONTENT_TYPE.APPLICATION.CSV],
  },
} as const;

export const MIME_TO_FILE_TYPE: Record<string, string> = {
  [CONTENT_TYPE.APPLICATION.JPG]: FILE_TYPE.JPEG,
  [CONTENT_TYPE.APPLICATION.PNG]: FILE_TYPE.PNG,
  [CONTENT_TYPE.APPLICATION.PDF]: FILE_TYPE.PDF,
  [CONTENT_TYPE.APPLICATION.XLS]: FILE_TYPE.XLS,
  [CONTENT_TYPE.APPLICATION.XLSX]: FILE_TYPE.XLSX,
} as const;

export const ENCODING_PREFIX_BASE64 = {
  JPEG: "data:image/jpeg;base64",
  PNG: "data:image/png;base64",
  PSD: "data:image/psd;base64",
};

export const EDITED_IMAGE_OPERATION = {
  SAVE: "SAVE",
  REVERT: "REVERT",
};

export const PROJECT_ALLOWED_FIELDS_ORDER_BY = ["name", "type", "createdAt", "isFavorite"] as const;
export const USER_ALLOWED_FIELDS_ORDER_BY = ["displayName", "name", "permissions", "createdAt"] as const;
export const USER_MAX_LIMIT_PER_PAGE = 40;

export const SORT_ORDER = {
  ASC: "asc",
  DESC: "desc",
} as const;

export const TRUE_STRING = "true";
export const NO_FILE_PROVIDED = "No File Provied";
export const MISSING_FILE_ID = "File ID is missing in the path after the bucket name.";
export const INVALID_GCS_FILE_URL = "Invalid GCS File URL Pattern";
export const CANCEL_WITHOUT_SAVING = "Cancel without saving";

export const URLS = {
  LOGIN: "/login",
  LOGOUT: "/login?from=logOut",
  TNC: "/acceptable-use-policy-overview",
  SELECT_BRAND: "/select-brand",
  HOME: "/home",
  SETTING: "/setting",
  TERMS_OF_SERVICE: "/acceptable-use-policy",
  PRIVACY_POLICY: "/privacy-policy",
  ACCESSIBILITY: "/accessibility",
  CONTACT_US: "/contact-us",
  RESPONSIBLE_AI: "/responsible-ai",
  LOGIN_ERROR: "/login?from=error",
  LOGIN_UNAUTH_ERROR: "/login?from=unauthorized",
  SIGN_IN_ERROR: "/api/auth/signin",
  CALLBACK_ERROR: "/api/auth/callback",
  SERVER_ERROR: "/api/auth/error",
  MANAGE_USERS: "/manage-users",
  TOOL_ANALYTICS: "/tool-analytics",
  CONCEPTING: "/concepting",
  PDP_COPY: "/pdp-copy",
  GENERATE_LIFESTYLE: "/generate-lifestyle",
  SAVED_PROJECTS: "/saved-projects",
  GENERATE_ASSETS: "/asset-generation",
  RAISE_REQUEST: "/raise-request",
  NO_ACCESS: "/no-access",
  RAI_PLAYBOOK:
    "https://partners.mdlz.com/sites/AIDAtraining/Shared%20Documents/Forms/AllItems.aspx?id=%2Fsites%2FAIDAtraining%2FShared%20Documents%2FGeneral%2FRAI%20Playbook%2FAIDA%2DPlaybook%2DJune%2D18%2D2025%5FFINAL%2Epdf&parent=%2Fsites%2FAIDAtraining%2FShared%20Documents%2FGeneral%2FRAI%20Playbook",
  VIDEO_GENERATION: "/video-generation",
  BACKGROUND_GENERATION: "/background-generation",
  CONTENT_GENERATION: "/content-generation",
  STORY_BOARD: "/story-board",
  IMAGE_EDITING: "/image-editing",
};

export const ROLES = {
  ADMIN: "admin",
  USER: "user",
  PARTNER: "partner",
} as const;

export const PERMISSIONS = {
  APPROVER: "approver",
  CONCEPTER: "concepter",
  CREATIVE: "creative",
  COPYWRITER: "copywriter",
  LIFESTYLE: "lifestyle",
  MANAGE_PARTNERS: "manage_partners",
  MANAGE_AGENCY_USERS: "manage_agency_users",
  ALL: "all",
  VIDEO_GENERATION: "video_generation",
  FUN_BACKGROUND_IMAGE: "fun_background_image",
  CONTENT_GENERATION: "content_generation",
  STORYBOARD: "storyboard",
  IMAGE_EDITING: "image_editing",
} as const;

export const ENTITY_COLLECTION_NAMES = {
  PROJECT: "project",
  PROJECT_OUTPUT: "project-output",
  GENERATED_CONCEPT: "generated-concept",
  PDP_OPTIONS: "pdp-options",
  USERS: "users",
  FEEDBACK: "feedback",
  AUDIT_LOGS: "audit-logs",
  MEDIA: "media",
  CHAT: "chat",
  CHAT_MESSAGES: "chat-messages",
} as const;

export const AUDIT_LOG_EVENT_NAMES = {
  TNC: "TNC",
  PROJECT_PATCH: "PROJECT_PATCH",
  SIGN_IN: "SIGN_IN",
  SIGN_OUT: "SIGN_OUT",
} as const;

export const USER_PROVISIONING_REQUEST_STATUS = {
  PENDING: "PENDING",
  APPROVED: "APPROVED",
  INITIATED: "INITIATED",
} as const;

export const ONBOARDING_STATUS = {
  INITIAL: 0,
  LOGIN_ACCESS_GRANTED: 1,
  DISPLAYED_CAROUSEL: 2,
} as const;

export const footerLinks: FooterLink[] = [
  {
    title: "ACCEPTABLE USE POLICY",
    url: URLS.TERMS_OF_SERVICE,
  },
  {
    title: "ACCESSIBILITY",
    url: URLS.ACCESSIBILITY,
  },
  {
    title: "CONTACT US",
    url: URLS.CONTACT_US,
  },
  {
    title: "RESPONSIBLE AI",
    url: URLS.RESPONSIBLE_AI,
  },
];

export const URL_PERMISSIONS_MAPPING: URL_PERMISSIONS_MAPPING_TYPE = {
  [URLS.CONCEPTING]: PERMISSIONS.CONCEPTER,
  [URLS.PDP_COPY]: PERMISSIONS.COPYWRITER,
  [URLS.GENERATE_LIFESTYLE]: PERMISSIONS.LIFESTYLE,
  [URLS.GENERATE_ASSETS]: PERMISSIONS.CREATIVE,
  [URLS.CONTENT_GENERATION]: PERMISSIONS.CONTENT_GENERATION,
  [URLS.MANAGE_USERS]: [PERMISSIONS.MANAGE_AGENCY_USERS, PERMISSIONS.MANAGE_PARTNERS],
};

export const termsAndConditions: TermsAndConditionsCardProps = {
  title: "AIDA Acceptable Use Policy",
  introductionTncLogin: [
    {
      id: 1,
      text: "To ensure a safe and enjoyable experience, please take a moment to",
    },
    {
      id: 2,
      text: "review the Acceptable Use Policy",
      type: "bold",
    },
    {
      id: 3,
      text: "before you start creating. We highlight important policies around ethical and responsible AI use, intended use, human oversight, and monitoring to ensure compliance with AIDA’s governance framework.",
    },
  ],
  introduction:
    "To ensure a safe and enjoyable experience, please review the Acceptable Use Policy below before you start creating. This includes important guidance around ethical and responsible AI use, intended use, human oversight, and activity monitoring to ensure compliance with AIDA’s governance framework.",
  introTitle: "Last Updated: 10 APRIL 2025",
  readThrough:
    "To read through the full document, we’ve added a Table of Contents. Click on each link to navigate to the section you’re interested in reading.",
  privacyPolicyTitle: "Full Acceptable Use Policy",
  sections: [
    {
      title: "Overview",
      content: [
        {
          id: 1,
          text: `This AIDA Acceptable Use Policy (“`,
        },
        {
          id: 2,
          text: "AUP",
          type: "bold",
        },
        {
          id: 3,
          text: `”) applies to all users (“`,
        },
        {
          id: 4,
          text: "you",
          type: "bold",
        },
        {
          id: 5,
          text: `”) of the Mondelez AIDA Platform (“`,
        },
        {
          id: 6,
          type: "bold",
          text: `AIDA`,
        },
        {
          id: 7,
          text: `”). By using AIDA you agree that you are authorized to use AIDA and have read and agree to comply with this AUP. To use AIDA you must be an authorized employee of Mondelez Global LLC or its affiliate (“`,
        },
        {
          id: 8,
          text: "Mondelēz",
          type: "bold",
        },
        {
          id: 9,
          text: "”) or an authorized agency, service provider, independent contractor, or consultant of Mondelēz. Failure to comply with this AUP may result in suspension or termination of your access to AIDA.",
        },
      ],
    },
  ],
};

export const TNC_ERROR_MESSAGE: string = "Please read the full Terms of Services, then check the box to Accept and Continue";

export const TNC_NOT_SCROLLED_MESSAGE: string =
  "You must scroll to the bottom of the Acceptable Use Policy and check the Agree button to continue. If you do not accept the Acceptable Use Policy, you cannot use the tool.";

export const TNC_TEXT = {
  CHECKBOX_AGREEMENT: "I have reviewed and agree to the full",
  ACCEPTABLE_USE_POLICY: "Acceptable Use Policy.",
  SCROLL_REQUIREMENT: "It is required that you must scroll through all terms, and then check the ",
  AGREE_TEXT: "'I agree'",
};

export const footerPageContent: Record<string, FooterPageContent> = {
  privacyPolicy: {
    title: "Privacy Policy",
    introTitle: "What you need to know",
    introText:
      "This document contains information about X, Y, and Z as of [DATE]. To read through the full document, we’ve added a Table of Contents. Click on each link to navigate to the section you’re interested in reading.",
    sections: [
      {
        id: "link-one",
        heading: "Link One",
        paragraphs: [
          {
            text: "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.",
          },
          {
            text: "Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo. Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia consequuntur magni dolores eos qui ratione voluptatem sequi nesciunt.",
          },
        ],
      },
      {
        id: "link-two",
        heading: "Link Two",
        paragraphs: [
          {
            text: "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.",
          },
          {
            text: "Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo. Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia consequuntur magni dolores eos qui ratione voluptatem sequi nesciunt.",
          },
        ],
      },
    ],
  },
  accessibility: {
    title: "Accessibility",
    introTitle: "",
    introText: `To help provide a fully accessible and optimized experience for users, AIDA has taken careful measures to help ensure an enhanced user experience. Whether the user is using assistive technologies such as a screen reader, magnifier, or other assistive technology to access the platform, AIDA aims for AA compliance and follow the <b><a href="https://www.w3.org/TR/WCAG22/" target="_blank"/>Web Content Accessibility Guidelines (WCAG) 2.2</a></b> as a reference for best practices.`,
    sections: [
      {
        id: "link-one",
        heading: "Our commitment to accessibility",
        paragraphs: [
          {
            text: `When we dreamed up AIDA, we knew that Responsible AI (RAI) had to be about user inclusion. ​The platform is built to promote equity, enhance user roles, and support creativity without replacing it. ​To that end, our unique approach to inclusive design within AIDA relies on accommodating a wide range of user perspectives to reduce bias with every click. ​Plus, our platform visual design leans light on cognitive load for users, with ample space and explanatory copy at every step through the Content Checker.* ​`,
          },
          {
            text: "<b>*Content checker :</b><br/> A proprietary tool that evaluates AI-generated text and images against RAI standards. A “Pass” means the asset meets Content Checker’s guidelines, while a “Fail” indicates that the asset requires review, with explanations provided. While the Content Checker streamlines compliance, all assets should still undergo a human-led legal review.",
          },
        ],
      },
      {
        id: "link-two",
        heading: "Designed for usability and inclusion",
        paragraphs: [
          {
            text: "The future of inclusive AI considers every single user from the beginning.",
          },
          {
            text: "Along with WCAG 2.2 compliance, we ensure that AIDA and its content are more accessible for individuals with disabilities and user-friendly for everyone. This means we prioritize clear color contrast, readable type and button sizes, ease of language, and intuitive interactions like focus order.",
          },
        ],
      },
      {
        id: "link-three",
        heading: "Ongoing improvements and feedback",
        paragraphs: [
          {
            text: "Accessibility is an ongoing effort, and we welcome your feedback. The future must be accessible, and AIDA is designed to help make that a reality, but this is only possible with your partnership as regular users of the platform.",
          },
          {
            text: `If you encounter any accessibility challenges, please <b><a href=${URLS.CONTACT_US} target="_blank">contact us</a></b> so we can continue improving AIDA for everyone.`,
          },
        ],
      },
    ],
  },
  responsibleAi: {
    title: "RESPONSIBLE AI (RAI)",
    introTitle: "THE ETHICAL HEART OF AIDA",
    introText: `<b>April 9, 2025</b><br/><br/>Gen AI is rapidly transforming our world, showing us its efficiencies and its benefits.<br/><br/>Here on AIDA, we have the opportunity to experience it firsthand and explore all the exciting and sometimes unexpected outputs a platform of this scale brings. But as humans entering a new world of creativity, it is also our collective job to embrace change and navigate all things new while staying true to our core. And that’s where Responsible AI (RAI) comes in. It’s a set of processes and rules to follow in the best interest of safeguarding Brand Safety and Brand Reputation.`,
    sections: [
      {
        id: "defining-responsible-ai",
        heading: "Defining Responsible AI",
        paragraphs: [
          {
            text: "We define RAI as the practice of building an AI system in a way that is ethical, explainable, transparent, and accountable. This is achieved by creating a RAI Governance Framework that establishes rules and guardrails to guide its use to minimize risk and harmful impacts to all. MDLZ will adhere to industry and customized processes that align with our values.",
          },
          {
            text: `For a deeper dive into our approach to RAI, <b><a href=${URLS.RAI_PLAYBOOK}>access the complete RAI Playbook.</a></b>`,
          },
        ],
      },
      {
        id: "governance-and-content-checks",
        heading: "RAI governance and content checks",
        paragraphs: [
          {
            text: "At the core of RAI are two distinct ways we establish policies, roles, and oversight mechanisms to ensure compliance with all our RAI Principles. This oversight makes all of us accountable, helping define decision-making, monitoring, and addressing ethical concerns and mitigating risks as they arise.",
          },
          {
            text: "First, our AIDA Governance Board (AGB), ensures the RAI program stays current with global legislation impacting Gen AI and addresses any concerns down to each individual brand or region or locality.",
          },
          {
            text: "Next, our Content Checker is a proprietary AIDA functionality that leverages Gen AI’s abilities to “see” and “hear” and “understand” text and image input and output in the system. It contains custom RAI rules and guidelines that inspect outputs from AIDA and reports them to you, the human operator, as a “Pass” or “Fail” to meet RAI standards.",
          },
          {
            text: "A “Pass” means the asset didn’t violate the Content Checker’s rules. A “Fail” means the asset has not met one or more of the Content Checker’s rules. “Fail” results will also come with explanations for the human user to consult and review. It’s important to note that all concepts and assets produced by AIDA should go through the standard advertising legal review, including reporting any fail results received during the process.",
          },
          {
            text: "This process is integral to RAI reviews and helps streamline and dramatically enhance the review process by seamlessly flagging non-compliant assets. Please keep in mind that assets should still undergo a human review to satisfy all RAI standards.",
          },
          {
            text: "<b>Examples of issues our Content Checker may flag:</b>",
            points: [
              "Non-inclusive language",
              "Intellectual property violations",
              "Promotion overconsumption in imagery",
              "Claims around nutritional value",
              "Harmful text or imagery around race, ethnicity, age group, religion, nationality",
            ],
          },
        ],
      },
      // {
      //   id: "examples-of-issues",
      //   heading: "EXAMPLES OF ISSUES OUR CONTENT CHECKER MAY FLAG:",
      //   points: [
      //     "Non-inclusive language",
      //     "Intellectual property violations",
      //     "Overconsumption in imagery",
      //     "Claims around nutritional value",
      //     "Cultural insensitivities",
      //   ],
      // },
      {
        id: "security-within-aida",
        heading: "Security within AIDA",
        paragraphs: [
          {
            text: "We have robust security measures around the AIDA processes, and protect the",
          },
          {
            text: "infrastructure it relies on from unauthorized access, misuse, or attacks. This includes strong log-in authentication and Access Control Mechanisms to ensure that only authorized users like you can interact with AIDA and its data. We also have incident response plans in place to quickly detect, contain, and recover from security breaches or other incidents. This means clear procedures for notifying affected individuals and relevant authorities in the event of an incident within AIDA.",
          },
          {
            text: `Remember that AIDA is a tool used by MDLZ teams and MDLZ agencies around the globe, so please be careful not to prompt or upload proprietary information. If you have any questions about whether content should be uploaded, please contact <b><a href="mailto:RAI@mdlz.com">RAI@mdlz.com</a></b>.`,
          },
        ],
      },
      {
        id: "respect-for-human-autonomy",
        heading: "Respect for human autonomy",
        paragraphs: [
          {
            text: "We fully respect human autonomy to enhance, not undermine a user's ability to make independent choices. This means we empower everyone with clear, understandable information about how AIDA functions. We also allow you to override or disagree with any information or output. Control is about choice, after all, so your ability to manage how you use AIDA is a top priority.",
          },
          {
            text: `AIDA is a collaborative effort, so if you ever encounter any issues within AIDA, feel free to <b><a href="https://mdlz.service-now.com/technology" target="_blank">report them</a></b> or send feedback or questions to <b><a href="mailto:RAI@mdlz.com">RAI@mdlz.com</a></b>.`,
          },
        ],
      },
    ],
  },
};

export const CONTACT_US_CONTENT = {
  TITLE: "Contact us",
  DESCRIPTION: "We’re here to help with questions and feedback about AIDA, RAI and Gen AI. Reach out below and we’ll get back to you soon.",
  GO_TO_SERVICE_NOW: "Go to service now",
};

export const CONTENT_FILE_TYPE = {
  APPLICATION_EXCEL: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  APPLICATION_PDF: "application/pdf",
  APPLICATION_XLS: "application/vnd.ms-excel",
  APPLICATION_PNG: "image/png",
  APPLICATION_JPEG: "image/jpeg",
};
export const PROJECT_TYPES = {
  CAMPAIGN: "campaign",
  LIFESTYLE: "lifestyle",
  PDP: "pdp",
  PDP_COPY: "pdp_copy",
  GENERATE_ASSETS: "assetGeneration",
  GENERATE_VIDEO: "videoGeneration",
  BACKGROUND: "background",
  CONTENT: "contentGeneration",
  STORYBOARD: "storyboard",
  EDIT_IMAGE: "editImage",
} as const;

export const BRAND_IDS = {
  CHIP: "chips",
  OREO: "oreo",
  MILKA: "milka",
  CADBURY: "cadbury_uk",
  LACTA: "lacta_br",
  OREO_BR: "oreo_br",
  CHIPS_DCOMM: "chips_us_dcomm",
  OREO_BE_US: "oreo_be_us",
  OREO_DCOMM_MX: "oreo_dcomm_mx",
  OREO_BE_MX: "oreo_be_mx",
  SOUR_PATCH_US: "sour patch kids-us",
  BELVITA: "belvita-us",
  WHEAT_THINS: "wheat thins-us",
  TRISCUIT: "triscuit-us",
  RITZ: "ritz-us",
  CLIF_BAR: "clif bar-us",
  HALLS: "halls-us",
  _7Days: "7days-us",
  BARNUMUS_ANIMANLS: "barnums animals-us",
  BETTER_CHEDDARS: "better cheddars-us",
  BISCOS: "biscos-us",
  BUILDERS: "builders-us",
  CHICKEN_IN_A_BISKIT: "chicken in a biskit-us",
  CRISPERS: "crispers-us",
  EASY_CHEESE: "easy cheese-us",
  ENJOY_LIFE_FOODS: "enjoy life foods-us",
  GOOD_THINS: "good thins-us",
  HANDI_SNACKS: "handi snacks-us",
  HONEY_MAID: "honey maid-us",
  HU: "hu-us",
  LORNA_DOONE: "lorna doone-us",
  LU: "lu-us",
  LUNA: "luna-us",
  MALLOMARS: "mallomars-us",
  NEWTONS: "newtons-us",
  NILLA: "nilla-us",
  NUTTER_BUTTER: "nutter butter-us",
  PERFECT: "perfect-us",
  PREMIUM: "premium-us",
  SARGENTO: "sargento-us",
  SWEDISH_FISH: "swedish fish-us",
  TATES_BAKE_SHOP: "tates bake shop-us",
  TEDDY_GRAHAMS: "teddy grahams-us",
  TOBLERONE: "toblerone-us",
  ZBAR: "zbar-us",
  GINGER_SNACKS: "ginger snaps-us",
  PINWHEELS: "pinwheels-us",
  SOCIABLES: "sociables-us",
  OREO_BE_DE: "oreo be-de",
  OREO_DCOMM_IN: "oreo_dcomm_in",
  OREO_BE_IN: "oreo_be_in",
  OREO_DCOMM_AU: "oreo_dcomm_au",
  OREO_BE_AU: "oreo_be_au",
} as const;

export type BrandId = (typeof BRAND_IDS)[keyof typeof BRAND_IDS];

export const dCommBrands: BrandId[] = [
  BRAND_IDS.OREO,
  BRAND_IDS.OREO_BR,
  BRAND_IDS.CHIPS_DCOMM,
  BRAND_IDS.OREO_DCOMM_MX,
  BRAND_IDS.OREO_DCOMM_IN,
  BRAND_IDS.OREO_DCOMM_AU,
];

// Define a mapping of project types to their allowed roles
export const projectRoleMap = {
  [PROJECT_TYPES.CAMPAIGN]: PERMISSIONS.CONCEPTER,
  [PROJECT_TYPES.GENERATE_ASSETS]: PERMISSIONS.CREATIVE,
  [PROJECT_TYPES.PDP]: PERMISSIONS.LIFESTYLE,
  [PROJECT_TYPES.PDP_COPY]: PERMISSIONS.COPYWRITER,
  [PROJECT_TYPES.CONTENT]: PERMISSIONS.CONTENT_GENERATION,
  [PROJECT_TYPES.STORYBOARD]: PERMISSIONS.STORYBOARD,
  [PROJECT_TYPES.EDIT_IMAGE]: PERMISSIONS.IMAGE_EDITING,
} as const;

// Reminder: when updating stages please stages order from isForwardStage used for project versioning
export const STAGE_NAMES = {
  BRIEF_UPLOAD: "brief_upload",
  CAMPAIGN_DETAILS_REVIEW: "campaign_details_review",
  CONCEPT_SELECTION: "concept_selection",
  PROJECT_IMAGE_LIST: "projectImageList",
  PROJECT_COPY_LIST: "projectCopyList",
  // APPROVED: "approved", // stage not used yet
  // REJECTED: "rejected", // stage not used yet
  ASSET_SELECTION: "assetSelection",
  ENTER_DESCRIPTION: "enter_description",
  USE_A_CONCEPT: "use_a_concept",
  VIDEO_PROJECT_CREATION: "videoProjectCreation",
  VIDEO_PROJECT_INPUTS: "videoProjectInputs",
  VIDEO_PROJECT_REVIEW: "videoProjectReview",
  BACKGROUND_PROJECT_OPTIONS: "projectOptions",
  CHAT_PROJECT_CREATION: "chatProjectCreation", // currently stage used only by blog/storyboard projects
} as const;

// Used to specify which input source its being used for project creation
export const INPUT_SOURCES = {
  BRIEF_UPLOAD: "brief_upload",
  ENTER_DESCRIPTION: "enter_description",
} as const;

export const ASSET_TYPES = {
  TEXT: "Text",
  IMAGE: "Image",
  VIDEO: "Video",
  CSV: "csv",
} as const;

export const gtinSampleApp = "gtin-sample-db";

export const ASSET_GENERATION_TYPES = {
  TEXT: "text",
  VISUAL: "visual",
  VISUAL_TEXT: "visual_text",
  VIDEO_SCRIPT: "video_script",
} as const;

export const ASSET_GENERATION_OPTION_TYPES = {
  DESCRIPTION: "description",
  BRIEF: "brief",
  CONCEPT: "concept",
} as const;

export const product_attribute_collection = "'product_attribute_collection'";

export const GENERATED_CONCEPT_USER_ACTION = {
  DEFAULT: "",
  CONTINUE: "continue",
  REGENERATE_ALL: "regenerate",
  MORE_LIKE_THIS: "moreLikeThis",
} as const;

export const ASSET_PREFIX = "AIDA";

export const UNNAMED_DELIVERABLE = "Unnamed Deliverable";

// GTM and GA Event and Page names
export const GTM_ASSET_TYPE_VALUES = {
  IMAGE: "Image",
  VIDEO_SCRIPT: "Video Script",
  TITLE_DESCRIPTION: "Title/Description",
  VISUAL: "Image",
  BLOG: "Blog",
};

// List of valid event names for GTM
export const VALID_GTM_EVENT_NAMES = {
  AIDA_LOGIN_BTN_CLICK: "AIDA_LOGIN_BTN_CLICK",
  TNC_CANCEL: "TNC_CANCEL",
  AIDA_TNC_BTN_CLICK: "AIDA_TNC_BTN_CLICK",
  CONTINUE: "CONTINUE",
  CANCEL_WITHOUT_SAVING: "CANCEL_WITHOUT_SAVING",
  DASHBOARD: "DASHBOARD",
  IMAGE_GENERATED: "IMAGE_GENERATED",
  IMAGE_CREATED: "IMAGE_CREATED",
  RAI_OUTPUT_CHECK: "RAI_OUTPUT_CHECK",
  RAI_INPUT_CHECK: "RAI_INPUT_CHECK",
  PDP_COPY_GENERATED: "PDP_COPY_GENERATED",
  ERROR_CONTINUE: "ERROR_CONTINUE",
  START_CONCEPTING: "START_CONCEPTING",
  GENERATE_LIFESTYLE_IMAGES: "GENERATE_LIFESTYLE_IMAGES",
  DRAFT_PDP_COPY: "DRAFT_PDP_COPY",
  CREATE_ASSETS: "CREATE_ASSETS",
  ADD_TEXT: "ADD_TEXT",
  SELECT_DIMENSIONS: "SELECT_DIMENSIONS",
  ADD_REFERENCE: "ADD_REFERENCE",
  TEXT: "TEXT",
  VISUAL: "VISUAL",
  VISUAL_TEXT: "VISUAL_TEXT",
  VIDEO_SCRIPT: "VIDEO_SCRIPT",
  OUTPUT_SELECTION: "OUTPUT_SELECTION",
  CHANNEL_SELECTION: "CHANNEL_SELECTION",
  DIMENSION_SELECTION: "DIMENSION_SELECTION",
  SPECIFICATION_SELECTION: "SPECIFICATION_SELECTION",
  VIDEO_LENGTH_SELECTION: "VIDEO_LENGTH_SELECTION",
  INSIGHT_ADDED: "INSIGHT_ADDED",
  INSIGHT_DELETED: "INSIGHT_DELETED",
  ENTER_DESCRIPTION: "ENTER_DESCRIPTION",
  USE_BRIEF: "USE_BRIEF",
  USE_CONCEPT: "USE_CONCEPT",
  CONCEPT_SELECTION: "CONCEPT_SELECTION",
  CONCEPT_CREATED: "CONCEPT_CREATED",
  USER_FEEDBACK: "USER_FEEDBACK",
  USER_LOGIN: "USER_LOGIN",
  PDP_COPIED: "PDP_COPIED",
  PREVIOUS: "PREVIOUS",
  NEXT: "NEXT",
  GENERATED_ASSETS: "GENERATED_ASSETS",
  CONCEPT_DOWNLOAD: "CONCEPT_DOWNLOAD",
  PDP_COPY_DOWNLOAD: "PDP_COPY_DOWNLOAD",
  TRANSLATION: "TRANSLATION",
  SELECT_CONCEPT_CONTINUE: "SELECT_CONCEPT_CONTINUE",
  VIDEO_SCRIPT_SELECTION: "VIDEO_SCRIPT_SELECTION",
  VISUAL_SELECTION: "VISUAL_SELECTION",
  ASSET_DOWNLOAD: "ASSET_DOWNLOAD",
  ASSETS_CREATED: "ASSETS_CREATED",
  PDP_COPY_CREATED: "PDP_COPY_CREATED",
  ASSET_COPIED: "ASSET_COPIED",
  REPLACE_IMAGE: "REPLACE_IMAGE",
  ASSET_GENERATION_TIME: "ASSET_GENERATION_TIME",
  IMAGE_GENERATION_RESTRICTED: "IMAGE_GENERATION_RESTRICTED",
  EVENT_OVERLAY: "EVENT_OVERLAY",
  FILE_UPLOAD: "FILE_UPLOAD",
  VIRTUAL_PAGE_LOAD: "VIRTUAL_PAGE_LOAD",
  LOGOUT: "LOGOUT",
  CONCEPT_COPIED: "CONCEPT_COPIED",
  SAVED_PROJECTS: "VIEW_SAVED_PROJECTS",
  LINK_CLICK: "LINK_CLICK",
  UPSCALE_CLICK: "UPSCALE_CLICK",
  IMAGE_DOWNLOAD: "IMAGE_DOWNLOAD",
  SELECT_PRODUCT_IMAGE: "SELECT_PRODUCT_IMAGE",
  VIDEO_GENERATED: "VIDEO_GENERATED",
  VIDEO_DOWNLOAD: "VIDEO_DOWNLOAD",
  EDIT_IMAGE: "EDIT_IMAGE",
  ASSET_EDITED: "ASSET_EDITED",
  BUTTON_CLICK_EVENT: "BUTTON_CLICK_EVENT",
  VIDEO_COPIED: "VIDEO_COPIED",
  BLOG_SUBMIT: "BLOG_SUBMIT",
  STORYBOARD_SUBMIT: "STORYBOARD_SUBMIT",
  BLOG_DOWNLOAD: "BLOG_DOWNLOAD",
  STORYBOARD_GENERATED: "STORYBOARD_GENERATED",
  STORYBOARD_DOWNLOAD: "STORYBOARD_DOWNLOAD",
  BLOG_COPIED: "BLOG_COPIED",
  BLOG_GENERATED: "BLOG_GENERATED",
  IMPROVE_TRANSLATION: "IMPROVE_TRANSLATION",
  BACK_TO_ORIGINAL: "BACK_TO_ORIGINAL",
  // Add more valid event names as needed after confguring them in GTM
};

export const GTM_EVENT_PAGE_NAMES = {
  HOME: "home",
  TERMS_AND_CONDITIONS: "Acceptable Use Policy Overview",
  UPLOAD_BRIEF: "Upload Project Brief",
  IMAGE_LIST: "Project Image list",
  IMAGE_REVIEW: "Image Review",
  IMAGES_READY: "YOUR IMAGES ARE READY",
  BEGIN_PROCESS: "Start Creating",
  CONCEPT_OPTIONS: "Concept Options",
  PROJECT_COPY_LIST: "Project Copy List",
  PROJECT_IMAGE_LIST: "Project Image List",
  LIFESTYLES: "Lifestyles",
  PROJECT_OPTIONS: "Explore Options",
  BRIEF_INPUTS: "Add Inputs",
  CONCEPT_REVIEW: "Concept write up",
  GENERATED_COPY: "Generated Copy",
  CAMPAIGN_DETAILS: "Campaign Details",
  START_CONCEPTING: "Start Concepting",
  GENERATE_LIFESTYLE_IMAGES: "Generate Lifestyle Images",
  CREATE_ASSETS: "Create Assets",
  DRAFT_PDP_COPY: "Draft PDP Copy",
  GENERATED_ASSETS: "Review Assets",
  START_CREATING: "Start Creating",
  CONFIRM_DETAILS: "Confirm Details",
  PROJECT_BRIEF: "Project Brief",
  SAVED_PROJECTS: "Saved Projects",
  CONTENT_GENERATION: "Content Generation",
  STORYBOARD: "Story Board",
  IMAGE_EDITING: "Image Editing",
  SELECT_OPTIONS: "Select options",
  SELECT_PROJECT_OPTIONS: "Select project options",
  GENERATE_IMAGES: "Generate images",
  GENERATE_IMAGES_STEP: "Generate images",
  IMAGE_REVIEW_STEP: "Image review",
  YOUR_IMAGES_ARE_READY_STEP: "Your images are ready",
};

export const DELIVERABLE_TYPE = {
  PRODUCT: "product",
  LIFESTYLE: "lifestyle",
  CLAIM: "claim",
};

export const HTTP_STATUS = {
  OK: 200,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
};

export const ERROR_PAGE_ROUTE = "/error-page";

export const enum workflowType {
  PDP_COPY = "pdpCopy",
  LIFESTYLE = "lifeStyle",
  CAMPAIGN = "campaign",
  ASSET_GENERATION = "assetGeneration",
  BACKGROUND_GENERATION = "backgroundGeneration",
  VIDEO_GENERATION = "videoGeneration",
}
export const SERVER_CALL_FAILED = "Server call failed";
export const NETWORK_ERROR = "Network error";
export const PSD_URL_NOT_AVAILABLE = "PSD URL its not avaialble in generated outputs, this may occur for older projects";
export const AGREE_AND_PROCEED = "Agree & proceed";
export const DOWNLOAD_ACTION = "download";

export const QUERY_PARAMS = {
  SAVED_PROJECTS: "savedProjects",
};
export const IMAGE_GENERATION_RESTRICTED = "IMAGE_GENERATION_RESTRICTED";
export const SET_PROJECT_REFINEMENT = "SET_PROJECT_REFINEMENT";
export const TOGGLE_IMAGE_GEN_ERROR = "TOGGLE_IMAGE_GEN_ERROR";

export const DS_API_PATHS = {
  TEXT_GENERATION: "/v1/text_generation",
  VIDEO_SCRIPT_GENERATION: "/video_script_generation",
  IMAGE_GENERATION: "/generate",
  PRODUCT_PLACEMENT: "/product_placement",
  BACKGROUND_GENERATION: "/background_generation",
  PDP_GENERATION: "/pdp_generation",
  OVERLAY_PSD: "/overlay_psd",
  UPSCALE: "/upscale",
  PROMPT_GENERATION: "/v1/prompt_generation",
  VIDEO_GENERATION: "/video_generation",
  VIDEO_TRANSCRIPT_GENERATION: "/video_transcript_generation",
  VIDEO_STATUS_CHECK: "check_status",
  IMAGE_DESCRIPTION: "/v1/image_description",
  NANO_BANANA_GENERATION: "/nano_banana_generation",
  CONTENT_GENERATION_CHAT: "/v1/chat",
  CONTENT_GENERATION_CHAT_STATUS: "/v1/chat/status",
  STORYBOARD_AGENT_CHAT: "/v1/storyboard_agent/chat",
  STORYBOARD_AGENT_CHAT_STATUS: "/v1/storyboard_agent/fetch_generations",
};

export const FONT_SIZE = {
  DEFAULT: 60, // if fontSize its not provided to DS overlay_psd api default its 60
  CLAIM: 70,
  PRODUCT: 40,
};

export const TERMS_OF_SERVICE_POLICY = {
  SECTION_ORDER: [
    "RAI_PRINCIPLES",
    "INTENDED_USE",
    "PROHIBITED_ACTIVITIES",
    "HUMAN_REVIEW",
    "USER_ACCOUNTS",
    "CONTENT_CHECKER",
    "UPDATE_TO_AUP",
    "CONTACT_INFO",
  ],
  RAI_PRINCIPLES: {
    TITLE: "1. Responsible AI Principles",
    PART_ONE:
      "This AUP is a guide for using AIDA in a manner consistent with the AIDA Responsible AI Governance Framework, including the Responsible AI Principles and AIDA documentation and instructions (“",
    PART_TWO: "RAI Governance Framework",
    PART_THREE:
      "”) which are Mondelēz's foundational values for the ethical and responsible use of AIDA. You must use AIDA in a manner that is consistent with the RAI Governance Framework at all times. You may read more about the RAI Governance Framework on the",
    PART_FOUR: "Responsible AI",
    PART_FIVE: "page.",
  },
  INTENDED_USE: {
    TITLE: "2. Intended Use",
    PART_ONE:
      "AIDA is an artificial intelligence tool that is intended to assist you with generating ideas, content and other materials that may be used in connection with advertising or marketing Mondelēz's and its brands' products in a manner that is consistent with the RAI Governance Framework. Using AIDA for any purpose other than for furthering Mondelēz's or its brands' advertising or marketing activities as directed by Mondelēz violates the RAI Governance Framework and is prohibited.",
  },
  PROHIBITED_ACTIVITIES: {
    TITLE: "3. Prohibited Activities",
    PART_ONE: "The following activities violate the RAI Governance Framework and are prohibited:",
    LIST: [
      {
        id: 1,
        text: "using AIDA in a manner that violates:",
        list: [
          { id: 2, text: "any applicable laws or regulations;" },
          { id: 3, text: "Mondelēz,s policies; or" },
          {
            id: 4,
            text: [
              { id: 5, text: "the AIDA documentation and instructions, available on the" },
              { id: 6, type: "link", link: URLS.RESPONSIBLE_AI, text: "Responsible AI" },
              { id: 7, text: "page;" },
            ],
          },
        ],
      },
      {
        id: 8,
        text: "inputting any of the following as a prompt, training data, or other input into AIDA:",
        list: [
          { id: 9, text: "images, video, or audio that shows or depicts a human face;" },
          {
            id: 10,
            text: "personal data or personally identifiable information (including, without limitation, such data or information protected by data protection or privacy laws);",
          },
          {
            id: 11,
            text: "any data, content, or other materials for which you do not have the necessary licenses, consents, or permission to use with AIDA globally for perpetuity and for any purpose in any medium or channel; or",
          },
        ],
      },
      {
        id: 12,
        text: "inputting any output from another generative AI platform into AIDA or using any output from AIDA as an input into another generative AI platform;",
      },
      {
        id: 13,
        text: "using AIDA to generate any output that:",
        list: [
          { id: 14, text: "is an unsubstantiated product claim;" },
          { id: 15, text: "is false or misleading;" },
          { id: 16, text: "promotes or encourages overconsumption or unhealthy eating habits;" },
          { id: 17, text: "depicts a human face;" },
          {
            id: 18,
            text: "contains any content that infringes or violates any third party's intellectual property, publicity, privacy, or other proprietary right;",
          },
          {
            id: 19,
            text: "is harmful or offensive, including any content that is discriminatory, harassing, racist, sexist, bigoted, prejudiced, hateful, malicious, violent, slanderous, libelous, abusive, or sexually explicit;",
          },
          {
            id: 20,
            text: "depicts prohibited activities related to illegal drugs, export control violations, illegal gambling or illegal arms trafficking;",
          },
          { id: 21, text: "implies or endorses any political parties or opinion-based movements; or" },
          { id: 22, text: "impersonates another person." },
        ],
      },
      {
        id: 23,
        text: [
          {
            id: 24,
            text: `combining AIDA with any hardware, software, or equipment that is not provided by Sapient Corporation d/b/a Publicis Sapient or its affiliate ("`,
          },
          { id: 25, type: "bold", text: "Publicis" },
          {
            id: 26,
            text: `”) without submitting such combination to Publicis for its review in accordance with Publicis' governance processes and obtaining written approval from Publicis for such combination; and`,
          },
        ],
      },
      {
        id: 27,
        text: "modifying AIDA (including any of its source code), without submitting such modifications to Publicis for review in accordance with Publicis' governance processes and obtaining written approval from Publicis for such modifications;",
      },
      {
        id: 28,
        text: "circumventing, disabling, or otherwise interfering with security-related features of AIDA, including attempting to bypass any measures of AIDA designed to prevent or restrict access to AIDA or any portion of AIDA;",
      },
      {
        id: 29,
        text: "deciphering, decompiling, disassembling, or reverse engineering any of the software comprising or in any way making up a part of AIDA;",
      },
      {
        id: 30,
        text: "uploading or transmitting (or attempting to upload or to transmit) any viruses, Trojan horses, spyware, or other software or malicious code that could impact the operation of AIDA or any systems, computer or other device;",
      },
      {
        id: 31,
        text: "interfering with, disrupting, or creating an undue burden on AIDA or the networks or systems on which AIDA is connected; and",
      },
      {
        id: 32,
        text: "making excessive requests on AIDA or otherwise generating more output than is necessary for the relevant business requirements;",
      },
      {
        id: 33,
        text: "using any output or other data generated by AIDA for training any other AI tools, models, or algorithms; and",
      },
      {
        id: 34,
        text: "using AIDA as part of any effort to compete with Publicis or otherwise use AIDA for any revenue-generating endeavor or commercial enterprise that competes with Publics; and",
      },
      {
        id: 35,
        text: "using AIDA for personal reasons or other than for furthering Mondelēz’s or its brands’ advertising or marketing activities as directed by Mondelēz.",
      },
    ],
    END: "The above list of prohibited activities is not exhaustive and there may be activities not listed above that violate the RAI Governance Framework or that are otherwise prohibited. You are responsible for ensuring that your activities do not violate the RAI Governance Framework or are not otherwise prohibited. If you are unsure whether an activity violates the RAI Governance Framework or are otherwise prohibited, consult with Mondelēz legal. ",
  },
  HUMAN_REVIEW: {
    TITLE: "4. Human Review and Oversight of Output",
    PARAGRAPH_ONE:
      "All output from AIDA must be thoroughly and adequately reviewed by a human. Do not copy and paste any output from AIDA directly into any final work product without reviewing and making any necessary revisions to remove any content that may infringe a third party's rights, exhibit bias, be offensive, false or misleading, or depict anything that may violate Mondelēz’s advertising policies or the RAI Governance Framework. Conduct at least the same quality control procedures on AIDA generated content as you would use on traditional human generated content.",
    PARAGRAPH_TWO:
      "Current copyright law limits the protection of AIDA generated outputs to only those with a sufficient amount of human authorship. Using AIDA generated outputs as a foundation, which is built upon with additional human-authored elements, will likely improve the protect-ability of the final work product.",
    PARAGRAPH_THREE:
      "In addition, you must submit all final work containing output from AIDA (as with any other advertising materials) for advertising legal review in accordance with your agency’s and Mondelēz’s standard processes, as applicable, before releasing to the public.",
  },
  USER_ACCOUNTS: {
    TITLE: "5. User Accounts",
    CONTENT: [
      { id: 36, text: `To access and use AIDA, you must have an active user login and password (“` },
      {
        id: 37,
        text: "User Account",
        type: "bold",
      },
      {
        id: 38,
        text: `”). You will (a) protect the confidentiality of your User Account and not share the password to your User Account with anyone; (b) be solely responsible for any activities that occur from the use or misuse of your User Account; and (c) immediately notify Mondelēz of applicable personnel changes that require a change to your User Account. Your User Account may be immediately suspended or terminated in the event of actual or reasonably suspected unauthorized use.`,
      },
    ],
  },
  CONTENT_CHECKER: {
    TITLE: "6. Content Checker and Monitoring of Activities",
    PARAGRAPH_ONE:
      "AIDA includes a mechanism to detect if your use may violate the RAI Governance Framework (“Content Checker”). If the Content Checker notifies you that your use violates the RAI Governance Framework and you elect to proceed notwithstanding such notice, you acknowledge that your use is in violation of the RAI Governance Framework. The Content Checker is not meant to replace human review and may not detect all potential violations of the RAI Governance Framework. The lack of a notice from the Content Checker does not mean that your use complies with the RAI Governance Framework or this AUP.",
    PARAGRAPH_TWO:
      "In addition, Mondelēz may, but is not obligated to, monitor your use of AIDA, including tracking, tagging, and logging your input, output, Content Checker notices of any potential violation of the RAI Governance Framework, and other activities on AIDA, in order to confirm your compliance with the RAI Governance Framework and this AUP. You may not remove or modify any monitoring devices or otherwise interfere with Mondelēz’ monitoring of your use of AIDA. If Mondelēz determines, at its sole discretion, that your use violates the RAI Governance Framework or this AUP, Mondelēz may terminate your access to AIDA.",
  },
  UPDATE_TO_AUP: {
    TITLE: "7. Updates to the AUP",
    PARAGRAPH_ONE:
      "Local laws and acceptable practices with respect to generative artificial intelligence will continue to evolve and, as a result, the RAI Governance Framework and this AUP may need be updated from time to time to reflect the evolution of such laws and acceptable practices. Any such updates shall be effective upon posting. The date of the last update is set forth above. Continuing to use AIDA following the posting of an update to this AUP constitutes your agreement to and acceptance of the update.",
  },
  CONTACT_INFO: {
    TITLE: "8. Contact Information",
    CONTENT: [
      {
        id: 39,
        text: "For any questions or concerns regarding the RAI Governance Framework or this AUP, please",
      },
      {
        id: 40,
        text: "contact us.",
        type: "link",
        link: URLS.CONTACT_US,
      },
    ],
  },
};

export const ACCEPT_AND_CONTINUE = "Accept & Continue";
export const CONTINUE_TEXT = "Continue";

export const MODAL_NAME = {
  ALERT: "Alert Modal",
  RAI_MODAL: "RAI Modal",
  PRODUCT_PLACEMENT_MODAL: "Product Placement Modal",
  VIDEO_MODAL: "Saved video modal",
};

export const MODAL_TYPE = {
  ERROR: "ERROR",
  TIMEOUT: "TIMEOUT",
  GOOGLE_TIMEOUT: "GOOGLE_TIMEOUT",
};

export const FILE_STATUS = {
  UPLOADED: "Uploaded",
  NOT_UPLOADED: "Not Uploaded",
};

export const AIDA_LOGO = "AIDA";
export const MDLZ_LOGO = "Mondelez";
export const COMPLETED = "Completed";
export const CURRENT = "Current";
export const ACTIVE = "Active";
export const SORT_INSTRUCTION = "Click the table heading buttons to sort the table by the data in its column.";

export const GA_ERROR_TYPE = {
  CTA: "CTA Error",
  FORM: "Form Validation Error",
  UPLOAD: "FIle Upload Error",
  MODAL: "Modal Error",
  RAI_MODAL: "RAI Validation Error",
  LOGIN: "Login error",
  CHARACTERS_MAX_LIMIT: "Character count exceeds error",
};
export const SKIP_TO_CONTENT = "Skip to Content";
export const REQUIREMENTS_INFO = "To SAVE a project, you must complete all steps of the process.";
export const PRODUCT_PLACEMENT_LOCATION_ERROR = "Product placement overlay";
export const VIDEO_OVERLAY = "Video player overlay";

export const NOT_FOUND_CODE = "404";
export const NOT_FOUND_TEXT = "This page could not be found.";
export const FOCUS_ELEMENT_TIMEOUT = 350;
export const FOCUS_ELEMENT_TIMEOUT_ZERO = 0;

export const PROJECT_COUNT_LOADER_ALLOWED_PAGES = [URLS.HOME, URLS.SAVED_PROJECTS];

export const DROP_ZONE_INSTRUCTIONS = {
  NAVIGATION_SUGGESTIONS: "Use up and down arrow keys to browse and Enter to select.",
  NO_SUGGESTIONS_AVAILABLE: "No suggestions available.",
};

export const LIST_STATUS_MESSAGES = {
  LOADING: "Loading...",
  EMPTY: "No result found",
};

export const BRAND_MARKET_CLASS = "brand-market-title-0";

export const CLICK_OUTSIDE_FEATURES_DEFAULT = {
  autoSelectOnOutsideClick: true,
  clearOnOutsideClick: true,
};

export const GTM_ALLOWED_PROJECT_ROLES = ["concepter", "lifeStyle", "pdpCopy"];

export const MAX_IMAGE_CACHE = 5;
export const CACHE_KEY_LIST = "CACHE_KEY_LIST";
export const LABEL_CLASS_NAME =
  "font-roboto_mono text-[0.875rem] leading-[2.0625rem] tracking-[-0.0175rem] uppercase font-bold not-italic text-primary";
export const ROBOTO_MONO_PRIMARY_TEXT =
  "font-roboto_mono tracking-[-0.0175rem] font-bold not-italic text-primary normal-case text-[1rem] mt-[0.75rem]";
export const HEADING_DESCRIPTION_CLASS =
  "text-mondelez-primary-purple text-[0.875rem] font-bold leading-[2.0625rem] tracking-[-0.0175rem] uppercase";

export const API_VERSIONS = {
  v1: "v1",
  v2: "v2",
  v3: "v3",
};

// upscaling size options
export const UPSCALING_SIZE_OPTIONS = {
  x2: "x2",
  x4: "x4",
  "2400_2400": "2400_2400",
};

export const BRAND_NAMES = {
  CADBURY: "cadbury",
  LACTA: "lacta",
};

export const IMAGEN_SUPPORTED_BRANDS = [BRAND_NAMES.CADBURY, BRAND_NAMES.LACTA];
export const ALPHA = "ALPHA";
export const OUTPUT_LANGUAGE = "OUTPUT Language";
export const DOWNLOAD = "Download";
export const DOWNLOAD_ALL = "Download all";
export const COPY = "Copy";
export const EDIT = "Edit";
export const TRY_AGAIN = "Try again";
export const CONTENT_GENERATION_ERROR_TITLE = "WE’RE SORRY.";
export const CONTENT_GENERATION_ERROR_DESCRIPTION = "We were unable to generate your content.";
export const CONTENT_IN_GENERATION = "YOUR CONTENT IS GENERATING";

export const VIDEO_BUCKET_FOLDER = "video_generations";
export const TITLE_KEY = "title";
export const RAI_WARNING_MODAL_LABEL = "We recommend making edits now";
export const RAI_WARNING_MODAL_DESCRIPTION =
  "Your input has not passed the Responsible AI check and the flagged elements may affect the quality of your output. By choosing to proceed, you accept all responsibility for the results.";
export const RAI_WARNING_MODAL_LIFESTYLE =
  "Your input has not passed the Responsible AI check and flagged elements may affect the quality of your output. By choosing to proceed, you accept all responsibility for the results.";
export const BUTTON_TEXT_AGREE_AND_PROCEED = "Agree & proceed";
export const BUTTON_TEXT_EDIT = "Edit";

export const IMAGE_CAPS = "IMAGE";
export const IMAGES_CAPS = "IMAGES";

export const AIDA_SUBSCRIPTION_TYPES = {
  UNIVERSAL: "universal",
  PREMIUM: "premium",
};

export const IMAGE_TEXT = {
  REPLACE: "Replace image",
  REMOVE: "Remove image",
  UPLOAD: "Upload",
  UPLOAD_DESC: "an image",
  ALLOWED_EXTENSIONS: "(.jpeg, .jpg, or .png)",
};

export const FILE_EXTENSIONS = {
  PDF: ".pdf",
};

export const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "svg", "image"];

export const PDF_TEXT = {
  REPLACE: "Replace File",
  REMOVE: "Remove File",
  ALLOWED_EXTENSIONS: "(.pdf)",
  UPLOAD_DESC: "or drag a file(s)",
};

export const VALIDATION_MESSAGE_TYPES = {
  RAI_SUCCESS: "rai-success",
  RAI_ERROR: "rai-error",
  CUSTOM_ERROR: "custom-error",
} as const;

export const VIDEO_INPUT_VALIDATION_ERRORS = {
  VIDEO_URL_ERROR: "video url must be a valid URL",
  IMAGE_GCS_URL_ERROR: "URL must be http(s):// or gs:// or s3:// (GCS/S3) and a valid path",
  IMAGE_EXTENSION_ERROR: "Image URL must contain a .jpg/.jpeg or .png file extension",
  PROMPT_REQUIRED_ERROR: "prompt is required and must be a non-empty string",
  LAST_FRAME_ERROR: "lastFrame cannot be provided without firstFrame.",
  VIDEO_DURATION_ERROR: "videoDuration too large.",
};

export const VIDEO_ARIA_LABEL = {
  DEFAULT: "Video player",
};

export const VIDEO_ALLOWED_ASPECT_RATIO = ["16:9", "9:16"] as const;

export const VIDEO_ALLOWED_RESOLUTION = ["720p", "1080p"] as const;

export const SIGNED_URL_TTL_MINUTES = 15;

export const CHAT_MESSAGE_TYPE = {
  TEXT: "text",
  PDF: "pdf",
};

export const CHAT_ROLES_TYPE = {
  ASSISTANT: "assistant",
  USER: "user",
};

export const CHAT_INTENT_TYPE = {
  GENERATE_BLOG: "generate_blog",
  GENERATE_FAQ: "generate_faq",
  ASK_QUESTION: "ask_question",
  GENERAL_CHAT: "general_chat",
};

export const CHAT_CONTENT_TYPE = {
  BLOG: "blog",
  FAQ: "faq",
};

export const CONVERSATIONAL_ENDPOINTS = ["content-generation", "story-board", "image-editing"];

export const BUTTON_TEXT = {
  CANCEL: "Cancel",
};

export const RAI_STATUS_OUTPUT_TITLE = "We recommend making edits now";
export const RAI_STATUS_OUTPUT_DESCRIPTION =
  "Your output has not passed the Responsible AI check. By choosing to proceed, you accept all responsibility for the results. If you want to Edit the article, you’ll be taken back to the conversation.";
export const RAI_STATUS_RECOMMENDATION_TITLE = "Recommendation:";
export const RAI_STATUS_OUTPUT_DESCRIPTION_STORYBOARD =
  "Your output has not passed the Responsible AI check. By choosing to proceed, you accept all responsibility for the results. Choose “Edit now” to adjust the image or caption.";
export const RAI_STATUS_LOADING = "We're checking that the content meets our Responsible AI criteria.";
export const RAI_STATUS_BUTTONS = {
  AGREE_PROCEED: "Agree & proceed",
  EDIT: "Edit the article",
  EDIT_NOW: "Edit now",
  REMOVE: "Remove",
  CLOSE: "Close",
};

export const IMAGE_CONTENT_TYPES = ".jpeg,.jpg,.png,image/jpeg,image/png";

export const KEYBOARD_KEYS = {
  ESCAPE: "Escape",
  TAB: "Tab",
  ARROW_DOWN: "ArrowDown",
  ARROW_UP: "ArrowUp",
};

export const ALLOWED_STREAM_FILE_TYPES = ["jpg", "jpeg", "png", "gif", "webp", "svg", "psd", "mp4", "pdf", "xls", "xlsx"];

export const FILE_UPLOAD_ASSET_TYPES = {
  BRIEF: "Brief",
  REFERENCE_ASSETS: "Reference Assets",
  GENERATED_ASSETS: "Generated Assets",
};
